/**
 * useEditorialProgress — consumes editorial_* activity events published by
 * signal_detector + editorial_proposer to drive the EditorialProgressModal.
 *
 * Symmetric to useBriefProgress: exposes { isRunning, minimized, currentStep,
 * stepStates, counters, latencyMs, minimize, restore, dismiss }.
 * Fed by the shared /api/events/stream SSE via useSystemActivity.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSystemActivity } from "@/hooks/useSystemActivity";

const DETECTORS = [
  "frequency_spike",
  "topic_acceleration",
  "regional_momentum",
  "pattern_convergence",
  "market_entry",
];

const STEP_KEYS = ["scan", "evidence", "draft", "publish"];

function emptyState() {
  return {
    isRunning: false,
    minimized: false,
    startedAt: null,
    endedAt: null,
    stepStates: {
      scan:     { status: "pending", detectorsDone: [] },
      evidence: { status: "pending", evidenceCount: 0 },
      draft:    { status: "pending", index: 0, total: 0, lastTitle: "" },
      publish:  { status: "pending" },
    },
    counters: {
      signals_detected: 0,
      strong_signals: 0,
      proposals_drafted: 0,
    },
    feed: [],
    error: null,
  };
}

const Ctx = createContext(null);

export function EditorialProgressProvider({ children }) {
  const [state, setState] = useState(emptyState);
  const activity = useSystemActivity();
  const seenIdsRef = useRef(new Set());
  const [now, setNow] = useState(Date.now());

  // Tick for latency display
  useEffect(() => {
    if (!state.isRunning) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state.isRunning]);

  useEffect(() => {
    const events = activity?.events || [];
    // Process new events (each event has an implicit .ts + .kind)
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (!e || !e.kind || !e.kind.startsWith("editorial_")) continue;
      const key = `${e.ts}-${e.kind}-${e.signal_id || e.detector || e.title || ""}`;
      if (seenIdsRef.current.has(key)) continue;
      seenIdsRef.current.add(key);
      setState((s) => reduce(s, e));
    }
  }, [activity?.events]);

  const latencyMs = state.startedAt
    ? (state.endedAt || now) - state.startedAt
    : 0;

  const value = useMemo(() => ({
    ...state,
    latencyMs,
    minimize: () => setState((s) => ({ ...s, minimized: true })),
    restore:  () => setState((s) => ({ ...s, minimized: false })),
    dismiss:  () => setState(emptyState),
  }), [state, latencyMs]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function reduce(s, e) {
  const next = { ...s, stepStates: { ...s.stepStates }, counters: { ...s.counters }, feed: [...s.feed] };
  const now = Date.now();
  switch (e.kind) {
    case "editorial_run_started": {
      const fresh = emptyState();
      fresh.isRunning = true;
      fresh.startedAt = now;
      fresh.stepStates.scan.status = "running";
      return fresh;
    }
    case "editorial_signal_detected": {
      next.counters.signals_detected += 1;
      if ((e.strength || 0) >= 0.7) next.counters.strong_signals += 1;
      const entity = firstEntityLabel(e.entities);
      next.feed = [
        { kind: "signal", label: `New signal: ${entity} · ${e.detector} (${(e.strength || 0).toFixed(2)})`, ts: now },
        ...next.feed,
      ].slice(0, 6);
      return next;
    }
    case "editorial_detector_finished": {
      const done = new Set(next.stepStates.scan.detectorsDone || []);
      done.add(e.detector);
      next.stepStates.scan = {
        ...next.stepStates.scan,
        detectorsDone: [...done],
        status: done.size >= DETECTORS.length ? "completed" : "running",
      };
      return next;
    }
    case "editorial_signals_done": {
      next.stepStates.scan = { ...next.stepStates.scan, status: "completed",
                                detectorsDone: [...DETECTORS] };
      next.stepStates.evidence = { ...next.stepStates.evidence, status: "running" };
      return next;
    }
    case "editorial_evidence_loaded": {
      next.stepStates.evidence = {
        ...next.stepStates.evidence,
        status: "completed",
        evidenceCount: (next.stepStates.evidence.evidenceCount || 0) + (e.evidence_count || 0),
      };
      next.stepStates.draft = { ...next.stepStates.draft, status: "running" };
      return next;
    }
    case "editorial_proposal_drafting": {
      next.stepStates.draft = {
        ...next.stepStates.draft,
        status: "running",
        index: e.index || 0,
        total: e.total || 0,
        signal_type: e.signal_type || "",
      };
      return next;
    }
    case "editorial_proposal_created": {
      next.counters.proposals_drafted += 1;
      next.stepStates.draft = { ...next.stepStates.draft, lastTitle: e.title || "" };
      next.feed = [
        { kind: "proposal", label: `New proposal: ${e.title}`, ts: now },
        ...next.feed,
      ].slice(0, 6);
      return next;
    }
    case "editorial_run_completed": {
      next.stepStates.draft = { ...next.stepStates.draft, status: "completed" };
      next.stepStates.publish = { ...next.stepStates.publish, status: "completed" };
      next.isRunning = false;
      next.endedAt = now;
      return next;
    }
    case "editorial_run_failed": {
      next.isRunning = false;
      next.endedAt = now;
      next.error = e.label || "run failed";
      return next;
    }
    default:
      return next;
  }
}

function firstEntityLabel(ent) {
  if (!ent || typeof ent !== "object") return "—";
  return ent.company || ent.topic || ent.region || Object.values(ent)[0] || "—";
}

export function useEditorialProgress() {
  const v = useContext(Ctx);
  return v || { ...emptyState(), latencyMs: 0,
    minimize: () => {}, restore: () => {}, dismiss: () => {} };
}
